<script setup lang="ts">
import ConfirmDialog from 'primevue/confirmdialog';
import { useActivities, useUtilities, useStringFunctions } from '@apparatix/composables';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import type { Activity, ActivityFilterSection, ActivityTypeName } from '@apparatix/schemas/Activities';
import { DialogGroups } from '@apparatix/constants';
import { Stack } from '@apparatix/library/core';
import HistoryAccordion from './HistoryAccordion.vue';
import SkeletonLoader from '@apparatix/library/SkeletonLoader.vue';
import ActivityCard from './ActivityCard.vue';

type Props = {
  sections: ActivityFilterSection[],
}

type Emits = {
  (e: 'section-load-more', sectionKey: string): void,
  (e: 'activity-updated', activity: Activity): void,
  (e: 'activity-deleted', id: number): void,
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const {
  completeActivity,
  confirmDeleteActivity,
  createOrUpdateActivity,
  deleteActivity,
  findParentRelationship,
  openDeferReminderDialog,
} = useActivities();
const { addActivityComponent, showModal } = useCreateActivity();

const { arrayHasValues, asKeyedItems } = useUtilities();
const { isEmptyString } = useStringFunctions();

type SkeletonShape = 'square' | 'rounded' | 'circle';

const loaderConfig = {
  rows: [
    { columns: 1, shape: 'rounded' as SkeletonShape, height: '2rem' },
    { columns: 1, shape: 'rounded' as SkeletonShape, height: '16rem' },
    { columns: 1, shape: 'rounded' as SkeletonShape, height: '2rem' },
  ],
};

const getLoadButtonLabel = (section: ActivityFilterSection) => {
  if (!section.immediateLoad && !arrayHasValues(section.activities)) {
    return 'Load';
  }

  return 'Load more';
};

const shouldDisableLoadMoreButton = (section: ActivityFilterSection) => {
  if (!section.immediateLoad && !arrayHasValues(section.activities)) {
    return false;
  }

  return isEmptyString(section.nextCursor);
};

const onEditActivity = (activity: Activity) => showModal(activity.type.name as ActivityTypeName, undefined, findParentRelationship(activity), activity);

const onCompletedChange = async (completed: boolean, activity: Activity) => {
  const updated = completed
    ? await completeActivity(activity)
    : await createOrUpdateActivity({ id: activity.id, completedAt: undefined });

  emit('activity-updated', updated);
};

const acceptDeleteActivity = async (series: boolean, id: number) => {
  await deleteActivity(id, series);
  emit('activity-deleted', id);
};

const onDeleteActivity = async (id: number, series: boolean) => {
  confirmDeleteActivity(series, () => acceptDeleteActivity(series, id));
};
</script>
<template>
  <Stack>
    <template v-for="section in props.sections" :key="section.key">
      <SkeletonLoader
        v-if="section.isVisible"
        :loading="section.isLoading"
        :rows="loaderConfig.rows"
        fade-after-load
      >
        <div>
          <HistoryAccordion
            :badge-severity="section.badgeSeverity"
            :title="section.filterName"
            :item-count="section.activities?.length ?? 0"
            :uuid="section.key"
            :load-more-disabled="shouldDisableLoadMoreButton(section)"
            :load-more-button-label="getLoadButtonLabel(section)"
            show-load-more-button
            @show-more="sectionId => emit('section-load-more', sectionId)"
          >
            <ActivityCard
              v-for="activity in asKeyedItems(section.activities)"
              :key="activity.key"
              :activity="activity"
              :parent-id="findParentRelationship(activity)?.id"
              :parent-type="findParentRelationship(activity)?.type"
              :relationship-buttons-keep-parent="false"
              show-all-relationships
              @edit="onEditActivity"
              @completed-change="onCompletedChange"
              @delete="onDeleteActivity"
              @defer="openDeferReminderDialog"
            />
          </HistoryAccordion>
        </div>
      </SkeletonLoader>
    </template>
  </Stack>
  <component :is="addActivityComponent" />
  <ConfirmDialog :group="DialogGroups.crm.activities.confirmDeleteActivity" />
</template>
